/**
 * Push Token Service (Web Version)
 * Handles web push subscriptions instead of Expo push tokens
 */

import { supabase } from '@services/supabase';
import { getExpoPushToken, getDeviceInfo } from './notificationService';

/**
 * Register web push subscription with backend
 * Stores the subscription in the database for sending push notifications
 */
export const registerPushToken = async (): Promise<boolean> => {
  try {
    console.log('📱 [PushToken:Web] Attempting to register web push subscription...');

    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      console.error('❌ [PushToken:Web] No authenticated user');
      return false;
    }

    // Get web push subscription (returns JSON string)
    const subscriptionString = await getExpoPushToken();

    if (!subscriptionString) {
      console.log('⏩ [PushToken:Web] No push subscription available');
      return false;
    }

    // Get device info
    const deviceInfo = await getDeviceInfo();

    // Store in database (same table as mobile, just different token format)
    const { error: insertError } = await supabase.from('user_push_tokens').upsert(
      {
        user_id: user.id,
        expo_push_token: subscriptionString, // Web push subscription JSON (column name from schema)
        device_id: deviceInfo.device_id,
        device_name: deviceInfo.device_name,
        device_os: 'web',
        is_active: true,
        last_used_at: new Date().toISOString(),
      },
      {
        onConflict: 'user_id,device_id',
      }
    );

    if (insertError) {
      console.error('❌ [PushToken:Web] Error storing push subscription:', insertError);
      return false;
    }

    console.log('✅ [PushToken:Web] Web push subscription registered successfully');
    return true;
  } catch (error) {
    console.error('❌ [PushToken:Web] Error in registerPushToken:', error);
    return false;
  }
};

/**
 * Deactivate web push subscription
 * Marks the subscription as inactive in the database
 */
export const deactivatePushToken = async (): Promise<void> => {
  try {
    console.log('🔕 [PushToken:Web] Deactivating web push subscription...');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return;
    }

    const deviceInfo = await getDeviceInfo();

    // Mark as inactive
    await supabase
      .from('user_push_tokens')
      .update({ is_active: false })
      .eq('user_id', user.id)
      .eq('device_id', deviceInfo.device_id);

    console.log('✅ [PushToken:Web] Web push subscription deactivated');
  } catch (error) {
    console.error('❌ [PushToken:Web] Error deactivating push token:', error);
  }
};

export default {
  registerPushToken,
  deactivatePushToken,
};
